import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { PageHeader, Panel, Pill, Loading, ErrorNote, Empty, formatDate } from '../components/ui';
import { navigate } from '../lib/router';
import { useRight } from '../lib/auth';

interface DocumentRow {
  id: string; title: string; document_type: string | null;
  version_id: string | null; version_number: number; page_count: number | null; is_scanned: boolean;
  ingest_status: string; ingest_error: string | null; filename: string;
  byte_size: number; uploaded_at: string; practice_name: string | null;
  item_count: string; pending_count: string; run_status: string | null;
  run_stage: string | null; run_done: number | null; run_total: number | null;
  run_started_at: string | null;
}

const STATUS_PILL: Record<string, { tone: string; label: string }> = {
  pending: { tone: 'draft', label: 'Queued' },
  processing: { tone: 'review', label: 'Processing' },
  ready: { tone: 'approved', label: 'Ready' },
  rejected: { tone: 'rejected', label: 'Rejected' },
  failed: { tone: 'rejected', label: 'Failed' },
};

/** Whether anything is processing the queue. See apps/api/src/jobs/heartbeat.ts. */
interface WorkerStatus {
  running: boolean;
  count: number;
  lastSeenSecondsAgo: number | null;
}

interface ReextractPlan {
  promptVersion: string;
  documents: { id: string; title: string; prompt_version: string | null; block_count: string }[];
  estimatePence: number;
  spend: { usedPence: number; capPence: number; remainingPence: number; exceeded: boolean };
}

interface Version {
  id: string; version_number: number; filename: string; page_count: number | null;
  is_scanned: boolean; ingest_status: string; created_at: string;
  is_current: boolean; uploaded_by_name: string | null;
  item_count: string; accepted_count: string;
}

function VersionHistory({ documentId }: { documentId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['versions', documentId],
    queryFn: () => api.get<{ versions: Version[] }>(`/api/documents/${documentId}/versions`),
  });

  if (isLoading) return <Loading what="Loading versions" />;
  const versions = data?.versions ?? [];
  if (versions.length <= 1) return null;

  return (
    <div className="table-wrap" style={{ marginTop: 10 }}>
      <table className="dt">
        <caption className="visually-hidden">Version history</caption>
        <thead>
          <tr>
            <th scope="col">Version</th><th scope="col">Uploaded</th>
            <th scope="col">Pages</th><th scope="col">Items accepted</th>
          </tr>
        </thead>
        <tbody>
          {versions.map((v) => (
            <tr key={v.id}>
              <th scope="row">
                v{v.version_number} {v.is_current ? <Pill tone="approved">Current</Pill> : null}
              </th>
              <td>
                {formatDate(v.created_at)}
                {v.uploaded_by_name ? <div className="field-hint">{v.uploaded_by_name}</div> : null}
              </td>
              <td>{v.page_count ?? '—'}</td>
              <td>{v.accepted_count} of {v.item_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="field-hint">
        Superseded versions stay readable. Their extractions remain tied to the document they
        were actually made from.
      </p>
    </div>
  );
}

export function Documents() {
  const canExtract = useRight('extraction');
  const canManage = useRight('settings');
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const versionRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [uploadError, setUploadError] = useState<unknown>(null);
  const [dragOver, setDragOver] = useState(false);
  const [showVersions, setShowVersions] = useState<string | null>(null);

  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: ['documents'],
    queryFn: () =>
      api.get<{ documents: DocumentRow[]; worker: WorkerStatus; now: string }>('/api/documents'),
    // Ingest and extraction run in the background, so the list refreshes
    // while anything is still in flight.
    refetchInterval: (query) => {
      const docs = query.state.data?.documents ?? [];
      const busy = docs.some(
        (d) => ['pending', 'processing'].includes(d.ingest_status) ||
               ['queued', 'running'].includes(d.run_status ?? ''),
      );
      return busy ? 3000 : false;
    },
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('title', file.name.replace(/\.pdf$/i, ''));
      form.append('file', file);
      return api.post('/api/documents', form);
    },
    onSuccess: () => {
      setUploadError(null);
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (e) => setUploadError(e),
  });

  const uploadVersion = useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const form = new FormData();
      form.append('file', file);
      return api.post(`/api/documents/${id}/versions`, form);
    },
    onSuccess: (_data, { id }) => {
      setUploadError(null);
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['versions', id] });
      setShowVersions(id);
    },
    onError: (e) => setUploadError(e),
  });

  const [renaming, setRenaming] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState('');

  const rename = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      api.patch(`/api/documents/${id}`, { title }),
    onSuccess: () => {
      setRenaming(null);
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });

  const extract = useMutation({
    mutationFn: (id: string) => api.post(`/api/documents/${id}/extract`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['documents'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/documents/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['retention'] });
    },
    onError: (e) => setUploadError(e),
  });

  const onFiles = (files: FileList | null) => {
    const file = files?.[0];
    if (file) upload.mutate(file);
  };

  const reextract = useQuery({
    queryKey: ['reextract-plan'],
    queryFn: () => api.get<ReextractPlan>('/api/documents/reextract'),
    enabled: canExtract,
  });

  const runReextract = useMutation({
    mutationFn: () => api.post<{ queued: number }>('/api/documents/reextract'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['reextract-plan'] });
    },
  });

  /**
   * Documents that are still on their way in, and documents that have arrived.
   *
   * A page that lists both together asks the reader to work out which is which
   * from a status pill, every time they look. The split is by whether anything
   * is still going to happen on its own: reading the PDF, or an extraction
   * already running. Everything else has settled — including the ones that
   * failed or were rejected, which are finished, and are exactly the ones
   * somebody needs to find in order to delete or replace them.
   */
  const documents = data?.documents ?? [];
  const isWorking = (doc: DocumentRow) =>
    ['pending', 'processing'].includes(doc.ingest_status) ||
    ['queued', 'running'].includes(doc.run_status ?? '');

  const working = documents.filter(isWorking);
  // Already newest-first from the server, which is the order someone looking
  // for what they just did wants.
  const settled = documents.filter((doc) => !isWorking(doc));

  /**
   * One card. The title inside it is an h3, not an h2, because each card now
   * sits inside a section whose heading is the h2 — skipping or repeating a
   * level is what makes a page unreadable to someone navigating by heading.
   * The styling is on the class rather than the tag, so it costs nothing.
   */
  const renderCard = (doc: DocumentRow) => {
        const status = STATUS_PILL[doc.ingest_status] ?? STATUS_PILL.pending!;
        const items = Number(doc.item_count);
        const pending = Number(doc.pending_count);
        const running = ['queued', 'running'].includes(doc.run_status ?? '');

        return (
          <article className="document-card" key={doc.id}>
            <div className="document-icon" aria-hidden="true">PDF</div>
            {renaming === doc.id ? (
              <form
                className="rename-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  const title = draftTitle.trim();
                  if (title) rename.mutate({ id: doc.id, title });
                }}
              >
                <label className="visually-hidden" htmlFor={`rename-${doc.id}`}>
                  Document title
                </label>
                <input
                  id={`rename-${doc.id}`}
                  className="rename-input"
                  value={draftTitle}
                  maxLength={300}
                  autoFocus
                  onChange={(e) => setDraftTitle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') setRenaming(null); }}
                />
                <div className="row-gap">
                  <button className="btn btn-primary btn-small" type="submit"
                    disabled={rename.isPending || !draftTitle.trim()}>
                    {rename.isPending ? 'Saving…' : 'Save'}
                  </button>
                  <button className="btn btn-ghost btn-small" type="button"
                    onClick={() => setRenaming(null)}>
                    Cancel
                  </button>
                </div>
                <ErrorNote error={rename.error} />
              </form>
            ) : (
              <h3 className="document-title">{doc.title}</h3>
            )}
            <div className="document-meta">
              {doc.document_type ?? 'Contract'}
              {doc.page_count ? ` · ${doc.page_count} pages` : ''}
              {doc.practice_name ? ` · ${doc.practice_name}` : ''}
              <br />
              Uploaded {formatDate(doc.uploaded_at)}
            </div>

            <div className="document-status row-gap">
              <Pill tone={status.tone}>{status.label}</Pill>
              {doc.is_scanned ? <Pill tone="review">Scanned</Pill> : null}
              {items > 0 ? (
                <Pill tone={pending ? 'review' : 'approved'}>
                  {items} items{pending ? `, ${pending} to review` : ''}
                </Pill>
              ) : null}
              {running ? <Pill tone="review">Extracting…</Pill> : null}
            </div>

            {running ? (
              <ExtractionProgress doc={doc} serverNow={data?.now} fetchedAt={dataUpdatedAt} />
            ) : null}

            {/*
              Reading has no batches to count, so it has no bar — but it is the
              phase that takes five or six minutes on a large contract, which
              makes it the one where someone most wants to know how long they
              have been waiting.
            */}
            {!running && ['pending', 'processing'].includes(doc.ingest_status) ? (
              <Elapsed
                since={doc.uploaded_at}
                serverNow={data?.now}
                fetchedAt={dataUpdatedAt}
                label={doc.ingest_status === 'processing' ? 'Being read for' : 'Waiting for'}
              />
            ) : null}

            {doc.ingest_error ? (
              <div className={doc.ingest_status === 'rejected' ? 'warn-box' : 'note-box'}>
                {doc.ingest_error}
              </div>
            ) : null}

            <div className="row-gap" style={{ marginTop: 12 }}>
              {items > 0 ? (
                <button
                  className="btn btn-primary btn-small"
                  onClick={() => navigate('breakdown', { doc: doc.id })}
                >
                  Open breakdown
                </button>
              ) : null}
              {/*
                Present from the moment a document exists, and disabled until
                it can actually run. It used to appear only once ingest had
                finished, so during the minute a PDF is being read the action
                someone came to the page for was simply absent — which reads
                as "this document cannot be extracted" rather than "not yet".
                The title says which it is.
              */}
              {canExtract ? (
                <button
                  className="btn btn-secondary btn-small"
                  onClick={() => {
                    // Re-reading is not free and not neutral: it costs a model
                    // call over the whole contract, and the review decisions
                    // already made do not carry across to the new run. Someone
                    // pressing "again" out of curiosity should find that out
                    // here rather than afterwards.
                    if (items > 0 && !window.confirm(reextractWarning(doc, items))) return;
                    extract.mutate(doc.id);
                  }}
                  disabled={extractDisabled(doc, extract.isPending)}
                  title={extractHint(doc)}
                >
                  {items > 0 ? 'Read it again' : 'Extract'}
                </button>
              ) : null}
              <button
                className="btn btn-ghost btn-small"
                onClick={() => navigate('ask', { doc: doc.id })}
              >
                Ask about this
              </button>
              {/*
                The stored PDF, opened as it was uploaded. Text-layer
                extraction can mangle tables and multi-column layouts, so
                being able to check an extraction against the real page is
                not optional — and every open is recorded in the audit log.
              */}
              <a
                className="btn btn-ghost btn-small"
                href={`/api/documents/${doc.id}/file`}
                target="_blank"
                rel="noopener"
              >
                Open the original
              </a>
              {canExtract ? (
                <>
                  <button
                    className="btn btn-ghost btn-small"
                    onClick={() => { setRenaming(doc.id); setDraftTitle(doc.title); }}
                  >
                    Rename
                  </button>
                  <button
                    className="btn btn-ghost btn-small"
                    disabled={uploadVersion.isPending}
                    onClick={() => versionRefs.current[doc.id]?.click()}
                    title="Upload a reissued version of this contract"
                  >
                    {uploadVersion.isPending ? 'Uploading…' : 'New version'}
                  </button>
                  <input
                    ref={(el) => { versionRefs.current[doc.id] = el; }}
                    type="file"
                    accept="application/pdf"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadVersion.mutate({ id: doc.id, file });
                      e.target.value = '';
                    }}
                  />
                </>
              ) : null}
              {Number(doc.version_number) > 1 ? (
                <button
                  className="btn btn-ghost btn-small"
                  onClick={() => setShowVersions(showVersions === doc.id ? null : doc.id)}
                >
                  {showVersions === doc.id ? 'Hide' : 'Show'} {doc.version_number} versions
                </button>
              ) : null}
              {/*
                The Retention panel tells administrators to delete expired
                contracts here, so there has to be a control here to do it.
                Deletion removes every version and its stored file, which is
                what makes an erasure request actually erase.
              */}
              {canManage ? (
                <button
                  className="btn btn-ghost btn-small danger"
                  disabled={remove.isPending}
                  onClick={() => {
                    const typed = window.prompt(
                      `Deleting "${doc.title}" removes every version, its extractions and the stored files. This cannot be undone.\n\nType the document title to confirm.`,
                    );
                    if (typed?.trim() === doc.title.trim()) remove.mutate(doc.id);
                    else if (typed !== null) window.alert('That did not match the title. Nothing was deleted.');
                  }}
                >
                  Delete
                </button>
              ) : null}
            </div>

            {showVersions === doc.id ? <VersionHistory documentId={doc.id} /> : null}
          </article>
        );
  };

  return (
    <>
      <PageHeader
        title="Documents"
        description="Contracts and service specifications. Upload a PDF and InBrief reads it, then a person reviews everything it found before it counts."
      />

      <WorkerWarning
        worker={data?.worker}
        waiting={(data?.documents ?? []).filter(
          (d) => ['pending', 'processing'].includes(d.ingest_status) || d.run_status === 'running',
        ).length}
      />

      {canExtract ? (
        <div
          className={`upload-zone${dragOver ? ' is-over' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); onFiles(e.dataTransfer.files); }}
          onClick={() => fileRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click(); }}
        >
          <div className="upload-icon" aria-hidden="true">↑</div>
          <div className="upload-text">
            {upload.isPending ? 'Uploading…' : 'Drop a contract PDF here, or choose a file'}
          </div>
          <div className="upload-subtext">
            PDF only, up to 32MB. InBrief does not accept documents containing patient details —
            uploads are checked and rejected if any are found.
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            hidden
            onChange={(e) => onFiles(e.target.files)}
          />
        </div>
      ) : (
        <div className="note-box">
          Your role does not include uploading or extracting contracts. You can read everything here.
        </div>
      )}

      {/*
        A prompt change leaves every previously extracted document on the old
        one. Re-extracting them individually is fine for three documents and
        useless for the two hundred a federation holds — but it also spends
        real money, so what it would cost is shown before the button, not
        discovered from an invoice.
      */}
      {canExtract && reextract.data && reextract.data.documents.length > 0 ? (
        <div className="warn-box reextract">
          <strong>
            {reextract.data.documents.length} document
            {reextract.data.documents.length === 1 ? ' was' : 's were'} extracted with an older
            version of the extraction prompt.
          </strong>
          <p>
            Re-extracting {reextract.data.documents.length === 1 ? 'it' : 'them'} on{' '}
            {reextract.data.promptVersion} would cost roughly{' '}
            £{(reextract.data.estimatePence / 100).toFixed(2)}, against{' '}
            £{(reextract.data.spend.remainingPence / 100).toFixed(2)} left in this month's
            budget. The existing items stay until the new run finishes, and review decisions
            are not carried across — the Contract Breakdown page shows what changed.
          </p>
          <ul className="reextract-list">
            {reextract.data.documents.map((d) => (
              <li key={d.id}>
                {d.title} <span className="field-hint">— prompt {d.prompt_version ?? 'unknown'}</span>
              </li>
            ))}
          </ul>
          {canManage ? (
            <button
              className="btn btn-primary btn-small"
              disabled={runReextract.isPending}
              onClick={() => runReextract.mutate()}
            >
              {runReextract.isPending
                ? 'Queueing…'
                : `Re-extract ${reextract.data.documents.length === 1 ? 'it' : 'all of them'}`}
            </button>
          ) : (
            <p className="field-hint">An administrator can start this.</p>
          )}
          <ErrorNote error={runReextract.error} />
        </div>
      ) : null}

      <ErrorNote error={uploadError} />
      <ErrorNote error={extract.error} />

      {isLoading ? <Loading what="Loading documents" /> : null}
      <ErrorNote error={error} />

      {data && data.documents.length === 0 ? (
        <Empty>No contracts yet. Upload one to get started.</Empty>
      ) : null}

      {working.length > 0 ? (
        <section aria-labelledby="docs-working">
          <h2 className="section-heading" id="docs-working">
            Being read <span className="section-count">{working.length}</span>
          </h2>
          <p className="field-hint section-intro">
            InBrief is reading these now. Extraction becomes available once it has finished.
          </p>
          <div className="document-grid">{working.map(renderCard)}</div>
        </section>
      ) : null}

      {settled.length > 0 ? (
        <section aria-labelledby="docs-previous">
          <h2 className="section-heading" id="docs-previous">
            Previously uploaded <span className="section-count">{settled.length}</span>
          </h2>
          <p className="field-hint section-intro">
            Open a contract, rename it, upload a reissued version, or delete it and everything
            extracted from it.
          </p>
          <div className="document-grid">{settled.map(renderCard)}</div>
        </section>
      ) : null}
    </>
  );
}

/**
 * Says when nothing is processing the queue.
 *
 * Ingest and extraction run on a separate worker process. Without one, an
 * upload is accepted, stored, queued — and then nothing happens, for ever. The
 * card says "Queued", which is true and reads as "any moment now".
 *
 * That is the worst failure this product has, because it is completely silent:
 * no error, no failed status, nothing in the interface that differs from a
 * working system. So it is stated here, unmissably, and only when it is
 * actually true — a running worker shows nothing at all.
 */
function WorkerWarning({ worker, waiting }: { worker?: WorkerStatus; waiting: number }) {
  if (!worker || worker.running) return null;

  const never = worker.lastSeenSecondsAgo === null;
  const ago = worker.lastSeenSecondsAgo ?? 0;
  const lastSeen = never
    ? 'No worker has ever reported in against this database.'
    : `The last worker reported in ${ago < 120 ? `${ago} seconds` : `${Math.round(ago / 60)} minutes`} ago.`;

  return (
    <div className="warn-box" role="status">
      <strong>Nothing is processing uploads.</strong>
      {' '}
      {waiting > 0
        ? `${waiting} document${waiting === 1 ? '' : 's'} ${waiting === 1 ? 'is' : 'are'} waiting and will stay that way until a worker is running.`
        : 'Anything uploaded will be stored and queued, and will stay queued.'}
      {' '}{lastSeen}
      <p className="field-hint" style={{ marginTop: 8 }}>
        Reading a PDF and extracting a contract take minutes and run outside the web request,
        on <code>npm run worker</code>. A serverless deployment has nowhere to run one, so it
        needs a host that runs processes. See DEPLOYMENT.md.
      </p>
    </div>
  );
}

/**
 * Whether the Extract button can do anything yet, and why not when it cannot.
 *
 * Kept as two small functions rather than inline conditions so the button's
 * enabled state and its explanation cannot drift apart — a disabled control
 * with no reason is worse than an absent one, because it looks like a fault.
 */
function extractDisabled(doc: DocumentRow, pending: boolean): boolean {
  if (pending) return true;
  if (['queued', 'running'].includes(doc.run_status ?? '')) return true;
  return doc.ingest_status !== 'ready';
}

/**
 * What re-reading actually costs, said before it happens.
 *
 * Extraction is not deterministic. Two runs over the same contract find
 * different numbers of items — which is the reason to offer this at all, and
 * equally the reason it is not a free retry: the second run can find less than
 * the first. Nothing here can promise an improvement, so it does not.
 *
 * The part people do not expect is that review decisions do not carry across.
 * Items belong to the run that produced them, so accepting forty of them and
 * then re-reading means accepting them again.
 */
function reextractWarning(doc: DocumentRow, items: number): string {
  return (
    `Read "${doc.title}" again from scratch?\n\n` +
    'Only do this if you think something was missed. Extraction is not ' +
    'deterministic — a second run may find more than the first, and may find ' +
    'less.\n\n' +
    `This costs another full read of the document, and the ${items} item(s) ` +
    'already extracted stay until the new run finishes and are then replaced. ' +
    'Any review decisions you have made do not carry across.'
  );
}

function extractHint(doc: DocumentRow): string {
  if (['queued', 'running'].includes(doc.run_status ?? '')) {
    return 'An extraction is already running on this document.';
  }
  switch (doc.ingest_status) {
    case 'ready':
      return 'Read the contract and pull out obligations, KPIs, deadlines and funding terms.';
    case 'rejected':
      return 'This upload was rejected, so there is nothing to extract.';
    case 'failed':
      return 'Reading the PDF failed. Upload it again, or a new version of it.';
    default:
      return 'Still reading the PDF. Extraction becomes available once that finishes.';
  }
}

/**
 * How far an extraction has got.
 *
 * The numbers come from the extractor as it works, not from a timer: pass two
 * structures the found items in batches and reports each one, so the bar moves
 * because something actually happened.
 *
 * Pass one has nothing to count — it is a single call over the whole document
 * — so the bar is indeterminate there and says what is happening instead of
 * inventing a percentage. That is the difference between a progress bar and a
 * decoration, and on a job that takes five or six minutes it is the difference
 * between waiting and reloading the page.
 */
/**
 * How long something has been running, counted from the server's clock.
 *
 * Not `Date.now() - startedAt`: those two come from different machines. A
 * phone a few minutes out would report a job as having started before it did,
 * or as taking far longer than it has — and on an extraction that legitimately
 * takes ten minutes, the elapsed time is the number somebody uses to decide
 * whether it has hung.
 *
 * So the gap is measured entirely on the server — `now` and `since` are both
 * its timestamps — and only the ticking afterwards comes from this machine,
 * measured from when the response arrived. Clock differences cancel out.
 */
function useElapsed(since: string | null, serverNow: string | undefined, fetchedAt: number): number {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!since) return;
    const timer = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [since]);

  if (!since || !serverNow) return 0;
  const atFetch = Date.parse(serverNow) - Date.parse(since);
  const sinceFetch = Date.now() - fetchedAt;
  // Clamped: a negative reading is a clock artefact, and counting up from a
  // negative number is worse than starting at zero.
  return Math.max(0, atFetch + sinceFetch);
}

function formatElapsed(ms: number): string {
  const total = Math.floor(ms / 1000);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  if (minutes) return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  return `${seconds}s`;
}

/**
 * The running time, under whatever is in progress.
 *
 * Deliberately not a live region. A screen reader announcing a new number
 * every second would make the rest of the page unusable; the progress bar
 * above it already reports position, and this is supporting detail somebody
 * reads when they choose to.
 */
function Elapsed({ since, serverNow, fetchedAt, label }: {
  since: string | null; serverNow: string | undefined; fetchedAt: number; label: string;
}) {
  const ms = useElapsed(since, serverNow, fetchedAt);
  if (!since) return null;
  return (
    <p className="elapsed">
      {label} <time>{formatElapsed(ms)}</time>
    </p>
  );
}

function ExtractionProgress({ doc, serverNow, fetchedAt }: {
  doc: DocumentRow; serverNow: string | undefined; fetchedAt: number;
}) {
  const total = doc.run_total ?? 0;
  const done = doc.run_done ?? 0;
  const determinate = total > 0;
  // Structuring is the long tail but not the whole job, so a full bar at the
  // end of it would sit at 100% through the saving phase. Reading is treated
  // as the first tenth and structuring as the rest.
  const percent = doc.run_stage === 'saving'
    ? 100
    : determinate
      ? Math.min(100, Math.round(10 + (done / total) * 85))
      : 8;

  const label = doc.run_stage === 'structuring' && determinate
    ? `Structuring what it found — batch ${Math.min(done + 1, total)} of ${total}`
    : doc.run_stage === 'saving'
      ? 'Saving the items and their citations'
      : doc.run_stage === 'reading'
        ? 'Reading the contract. This is the long part — five or six minutes on a large one.'
        : 'Queued, waiting for a worker to pick it up';

  return (
    <div className="extract-progress">
      <div
        className={`extract-bar${determinate ? '' : ' is-indeterminate'}`}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        {...(determinate ? { 'aria-valuenow': percent } : {})}
        aria-label={`Extraction progress: ${label}`}
      >
        <span style={{ width: `${percent}%` }} />
      </div>
      <p className="extract-progress-label">{label}</p>
      <Elapsed
        since={doc.run_started_at}
        serverNow={serverNow}
        fetchedAt={fetchedAt}
        label="Running for"
      />
    </div>
  );
}
